import { useI18n } from '@react/hooks';
import { AspectCard } from './AspectCard';
import type { AspectCardData } from '@/features/aspects/types';

function LayersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

interface AspectPanelProps {
  aspects: AspectCardData[];
  isLoading: boolean;
  onNavigate: (data: AspectCardData) => void;
}

export function AspectPanel({ aspects, isLoading, onNavigate }: AspectPanelProps) {
  const { t } = useI18n();

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-rail-foreground">
        {t('layout.rail.aspects')}
        {aspects.length > 0 && (
          <span className="ml-1.5 text-xs text-muted-foreground">({aspects.length})</span>
        )}
      </h3>
      <p className="text-xs text-muted-foreground">{t('rail.aspects.readOnlyNotice')}</p>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse p-3 rounded-lg border border-rail-border">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 bg-muted rounded-md" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && aspects.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <LayersIcon className="h-8 w-8 text-muted-foreground/50 mb-2" />
          <p className="text-sm font-medium text-muted-foreground">{t('rail.aspects.emptyTitle')}</p>
          <p className="text-xs text-muted-foreground/70 mt-1 max-w-[200px]">
            {t('rail.aspects.emptyDescription')}
          </p>
        </div>
      )}

      {!isLoading && aspects.length > 0 && (
        <div className="space-y-2">
          {aspects.map((aspect) => (
            <AspectCard
              key={aspect.id}
              data={aspect}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
